/**
 * Post-Add frame nudge (upsell plan #1, owner 2026-08-15) — the kiosk
 * "would you like fries with that?" moment. Fires ONCE per order, only when
 * the print just added has a SAME-SIZE frame in the catalogue (5×7/8×12 on
 * C-Type, 8×10–20×30 on fine art; a 4×6 customer is never asked), never on
 * multi-photo adds, never during wizard applies, never at checkout.
 *
 * One question, two buttons. "See it framed" opens the existing
 * AddFrameDialog (colour, matting explainer, real preview, confirm) — this
 * sheet only invites; the proven dialog does the selling. "No thanks" closes
 * every frame prompt for the session.
 */
import { framedProduct, type FramedProduct, type PricingData } from '@rpl/pricing';
import { frameExplainer } from '../cart/frame/frameOptions';
import { formatMoney } from '../../lib/format';
import type { LocalPhoto } from '../../lib/types';
import { ModalShell } from './ModalShell';

export function FrameNudgeSheet({
  pricing,
  offer,
  photo,
  printPrice,
  onSee,
  onClose,
}: {
  pricing: PricingData;
  offer: FramedProduct;
  photo: LocalPhoto | undefined;
  /** What the loose print costs now — the sheet shows the DELTA to framed. */
  printPrice: number;
  onSee: () => void;
  onClose: () => void;
}) {
  // Resolve through the engine so a stale offer can never show a wrong price.
  const price = framedProduct(pricing, offer.frameId).price;
  const delta = Math.max(0, price - printPrice);
  const detail = frameExplainer(pricing, offer).detail;
  return (
    <ModalShell onClose={onClose} width={400} label="Frame this print?" className="fnudge">
      <div className="fnudge__stage" aria-hidden>
        <div className="fnudge__frame">
          <div className="fnudge__matt">
            {photo?.previewUrl ? (
              <img className="fnudge__img" src={photo.previewUrl} alt="" />
            ) : (
              <div className="fnudge__img fnudge__img--ph" />
            )}
          </div>
        </div>
      </div>
      <div className="fnudge__body">
        <div className="fnudge__title">Frame this print?</div>
        <p className="fnudge__detail">{detail} Ready to hang.</p>
        <button type="button" className="btn btn--gold fnudge__cta" onClick={onSee}>
          See it framed · +{formatMoney(delta)}
        </button>
        <button type="button" className="fnudge__no" onClick={onClose}>
          No thanks
        </button>
      </div>
    </ModalShell>
  );
}
